// TeamLiveMemberSurface.tsx — /live/:userId, one person's live standup as a PAGE. An inverted
// pyramid, high level to low level, so the reader can stop at whatever depth satisfies them:
//
//   apex    the state line, one distilled sentence of where this person is
//   level 1 what they're on now, blocked, near your work (viewer-relative)
//   level 2 workstreams, the last few days as narrative (status + progress)
//   level 3 recent — the 3 newest touchpoints, given weight so the reader can stop here
//   level 4 the record, every remaining touchpoint day-grouped, with traceability
//
// The feed reads like a calendar: what just happened, then the rest of the last 24 hours. The rail
// (left on desktop) carries reference data only — the at-a-glance rows, near-your-work, and the
// source rollup split into connected / not-connected; nothing in the rail is part of the narrative.
//
// Deliberately NOT a profile (skills/projects/stats live in People's drawer). It slices the cached
// board query (no extra fetch — the route wrapper owns the hook). DISTINCT from the retrospective
// standup. Redesign port 2026-07: same structure, Tailwind under the `.rd` root (the prototype has
// no member page — this restyles prod's IA in the approved vocabulary).

import type { TeamLiveMember, TeamLiveItem, TeamLiveThread, TeamLiveSource } from '../../hooks/useTeamLiveSurface';
import { SourceIcon } from '../shared/SourceIcon';
import { siteLabel } from '../../lib/sites';
import { ACTIVE_SOURCES, TEAM_SHARED_OAUTH_SOURCES } from '../../lib/sources';
import { relativeTime, formatBriefingDate } from '../../lib/formatting';
import { AvatarSq, ActDot, activity, LiveStatement, BlockedCallout, Trace, InMeeting } from './live-shared';

interface TeamLiveMemberSurfaceProps {
  member: TeamLiveMember;
  generatedAt: string | null;
  you: boolean;
  // Every site connected by ANY member of the team, so a team-shared install (Slack workspace,
  // Notion) is never mistaken for a personal gap. See TeamLiveMemberRoute.
  teamConnectedSites?: string[];
  onNav?: (id: string) => void;
}

// Split the member's sources into what's wired up and what isn't. The backend's sources[] is the
// union of the member's connected providers and whatever produced visible 24h activity — so it can
// only ever describe sources that EXIST for this person. The absent ones are derived here by
// diffing the connectable catalog (ACTIVE_SOURCES, the same list the Sources surface renders).
//
// Two things this deliberately does NOT do:
//  - claim a team-shared source (Slack, Notion) is missing when a teammate carries the install. We
//    can't upgrade it to "connected" either — the backend never said so — so it's simply omitted.
//  - fabricate a connection. Synthetic rows are always { connected: false, events_24h: 0 }, which
//    the backend cannot itself emit (its keys are connected-or-active), so that combination is free
//    to mean exactly one thing: never connected.
//
// bug-518: 24h activity counts as connected even when the flag disagrees. A source that captured 11
// times today IS wired up; `connected` only ever meant "has an integrations row", which is strictly
// narrower — claude-code connects by minting an api_keys row, so it reported false forever. Splitting
// on the flag alone filed an actively-capturing source under "Not connected" directly above its own
// "last active 1h ago". The backend fix makes the flag honest for claude-code; this makes the split
// structurally incapable of stating that contradiction if another source is ever missed the same way.
export function splitSources(sources: TeamLiveSource[], teamConnectedSites: string[]): { connected: TeamLiveSource[]; missing: TeamLiveSource[] } {
  const wired = (s: TeamLiveSource) => s.connected || s.events_24h > 0;
  const present = new Set(sources.map((s) => s.site));
  const teamCovered = new Set(teamConnectedSites);
  const absent: TeamLiveSource[] = ACTIVE_SOURCES
    .filter((site) => !present.has(site))
    .filter((site) => !(TEAM_SHARED_OAUTH_SOURCES.includes(site) && teamCovered.has(site)))
    .map((site) => ({ site, connected: false, events_24h: 0, last_activity_at: null }));
  return {
    connected: sources.filter(wired),
    missing: [...sources.filter((s) => !wired(s)), ...absent].sort((a, b) => siteLabel(a.site).localeCompare(siteLabel(b.site))),
  };
}

// A quiet sans section heading (label + optional count). Whitespace + this heading carry the structure.
function SectionHead({ label, count }: { label: string; count?: number }) {
  return (
    <div className="mb-2.5 flex items-baseline gap-2">
      <span className="text-[15px] font-semibold">{label}</span>
      {typeof count === 'number' && <span className="font-['DM_Mono'] text-[10px] text-muted-foreground">{count}</span>}
    </div>
  );
}

const THREAD_STATUS: Record<TeamLiveThread['status'], { dot: string; label: string }> = {
  shipped: { dot: 'grn', label: 'Shipped' },
  in_progress: { dot: 'acc', label: 'In progress' },
  discussed: { dot: 'cyan', label: 'Discussed' },
  paused: { dot: 'amb', label: 'Paused' },
};
const THREAD_DOT: Record<string, string> = {
  grn: 'bg-[#62725c]', acc: 'bg-acc', cyan: 'bg-[#527c90]', amb: 'bg-[#c99b52]',
};

// Calendar-day label for the record's group headers, relative to the (possibly frozen) clock.
function dayLabel(iso?: string | null): string {
  if (!iso) return 'EARLIER';
  const d = new Date(iso);
  const now = new Date(Date.now());
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOfDay(now) - startOfDay(d)) / 86400000);
  if (diffDays <= 0) return 'TODAY';
  if (diffDays === 1) return 'YESTERDAY';
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }).toUpperCase();
}

// Group newest-first items into contiguous day buckets, preserving order.
function groupByDay(items: TeamLiveItem[]): Array<{ label: string; items: TeamLiveItem[] }> {
  const groups: Array<{ label: string; items: TeamLiveItem[] }> = [];
  for (const item of items) {
    const label = dayLabel(item.updated_at);
    const last = groups[groups.length - 1];
    if (last && last.label === label) last.items.push(item);
    else groups.push({ label, items: [item] });
  }
  return groups;
}

// One source row, used by both halves of the split: icon, label (workspace Slack says so in text —
// D2), and a 24h rollup line. Quiet connected sources read "connected · quiet today"; an
// activity-only source (no integration row) reads its activity without claiming a connection. The
// rollup is emitted as no-wrap clauses so that in the narrow rail it breaks at the separator rather
// than orphaning a word ("last active May / 4").
function SourceRow({ source }: { source: TeamLiveSource }) {
  const meta =
    source.events_24h > 0
      ? [`${source.events_24h} in the last day`, `last active ${relativeTime(source.last_activity_at ?? null)}`]
      // No activity and no connection can only be a catalog row synthesized by splitSources — the
      // backend never emits that pair. It carries no rollup line at all: the "Not connected" header
      // above it already says the only thing there is to say.
      : source.connected ? ['connected · quiet today'] : [];
  return (
    <div className="flex items-start gap-2.5 py-1.5">
      <span className="mt-0.5 shrink-0"><SourceIcon id={source.site} size={18} /></span>
      <div className="min-w-0">
        <p className="text-[13px] leading-5">{source.workspace ? `${siteLabel(source.site)} · workspace` : siteLabel(source.site)}</p>
        {meta.length > 0 && (
          <p className="text-[12px] leading-4 text-muted-foreground">
            {meta.map((clause, i) => (
              <span key={clause}>
                {i > 0 && ' '}
                <span className="whitespace-nowrap">{clause}{i < meta.length - 1 ? ' ·' : ''}</span>
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
}

function ThreadRow({ thread }: { thread: TeamLiveThread }) {
  const s = THREAD_STATUS[thread.status] ?? { dot: 'acc', label: thread.status };
  return (
    <div className="border-b border-border py-3 last:border-0">
      <p className="text-[15px] font-medium leading-5">{thread.name}</p>
      <p className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground">
        <span className={`size-1.5 shrink-0 rounded-full ${THREAD_DOT[s.dot] ?? 'bg-acc'}`} />
        {s.label}{thread.last_touched ? ` · ${relativeTime(thread.last_touched)}` : ''}
      </p>
      {thread.note && <p className="mt-1 text-sm leading-5 text-muted-foreground">{thread.note}</p>}
      <Trace trace={thread.trace} />
    </div>
  );
}

// One highlighted touchpoint — the same fields as a record row, given the weight of a workstream
// (larger title, no timeline rail) so the three newest read as the headline rather than as list
// item #1 of 40. Day headers are deliberately absent: these are the newest, the timestamp says when.
function HighlightRow({ item }: { item: TeamLiveItem }) {
  return (
    <div className="flex items-start gap-3 border-b border-border py-3 last:border-0">
      <span className="mt-0.5 shrink-0"><SourceIcon id={item.site ?? ''} size={20} /></span>
      <div className="min-w-0">
        <p className="text-[15px] font-medium leading-5">{item.title}</p>
        {item.summary && <p className="mt-1 text-sm leading-5 text-muted-foreground">{item.summary}</p>}
        <Trace trace={item.trace} withFile />
        <p className="mt-1 font-['DM_Mono'] text-[10px] text-muted-foreground">
          {item.site ? `${siteLabel(item.site)} · ` : ''}{relativeTime(item.updated_at ?? null)}
        </p>
      </div>
    </div>
  );
}

function RecordTimeline({ items }: { items: TeamLiveItem[] }) {
  return (
    <>
      {groupByDay(items).map((group) => (
        <div key={group.label} className="mt-4 first:mt-0">
          <p className="font-['DM_Mono'] text-[9px] uppercase tracking-[.16em] text-muted-foreground">{group.label}</p>
          <div className="mt-1.5">
            {group.items.map((item) => (
              <div key={item.id} className="flex items-start gap-3 py-2">
                <span className="flex shrink-0 flex-col items-center self-stretch">
                  <SourceIcon id={item.site ?? ''} size={16} />
                  <span className="mt-1.5 w-px flex-1 bg-border" />
                </span>
                <div className="min-w-0 pb-1">
                  <p className="text-[14px] leading-5">{item.title}</p>
                  {item.summary && <p className="mt-0.5 text-[13px] leading-5 text-muted-foreground">{item.summary}</p>}
                  <Trace trace={item.trace} withFile />
                  <p className="mt-0.5 font-['DM_Mono'] text-[10px] text-muted-foreground">
                    {item.site ? `${siteLabel(item.site)} · ` : ''}{relativeTime(item.updated_at ?? null)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

export function TeamLiveMemberSurface({ member, generatedAt, you, teamConnectedSites, onNav }: TeamLiveMemberSurfaceProps) {
  const act = activity(member.last_active);
  const date = generatedAt ? formatBriefingDate(generatedAt).toUpperCase() : null;
  const items = member.recent ?? [];
  const threads = member.threads ?? [];
  const blockers = member.blockers ?? [];
  const sources = member.sources ?? [];
  // Only split once the backend actually sent a sources[] — an older backend omits the field, and a
  // bare catalog diff there would read as "connected to nothing", which is a claim we can't make.
  const { connected: connectedSources, missing: missingSources } =
    sources.length > 0 ? splitSources(sources, teamConnectedSites ?? []) : { connected: [], missing: [] };
  // recent[] arrives newest-first from the BFF (ordered by last_captured_at desc), so the first
  // three ARE the three most recent — the highlight — and the tail is the rest of the last 24h.
  const highlights = items.slice(0, 3);
  const rest = items.slice(3);
  const tools = new Set(items.map((i) => i.site ?? 'unknown')).size;
  const quiet = !member.focus && !items.length && !threads.length && !blockers.length;

  const stateText = member.state_line
    ?? (quiet ? 'A quiet stretch. Nothing captured recently.'
      : `Working across ${tools || 'their'} tool${tools !== 1 ? 's' : ''}. Details below.`);
  const ctx = [member.project && { lab: 'project', v: member.project }, member.repo && { lab: 'repo', v: member.repo }, member.branch && { lab: 'branch', v: member.branch }].filter(Boolean) as Array<{ lab: string; v: string }>;

  return (
    <div className="v2-content rd p-0 bg-background font-['DM_Sans'] text-foreground">
      <div className="mx-auto max-w-[1200px] px-4 pb-16 pt-4 sm:px-7 lg:px-10">
        {/* masthead — full width: crumb, identity byline, apex state line, context chips */}
        <div className="flex items-start justify-between gap-4 border-b border-border pb-3">
          <span className="font-['DM_Mono'] text-[10px] uppercase tracking-[.16em] text-muted-foreground">
            Live standup{date ? ` · ${date}` : ''}
          </span>
          <button
            className="rounded-full border border-border bg-card px-3.5 py-1.5 text-sm text-muted-foreground transition hover:border-foreground hover:text-foreground"
            onClick={() => onNav?.('live')}
          >
            ← All standups
          </button>
        </div>
        <div className="mt-6 flex items-center gap-3">
          <AvatarSq seed={member.user_id} name={member.display_name} you={you} />
          <div className="min-w-0">
            <p className="text-[15px] font-semibold leading-5">
              {member.display_name}
              {you && <span className="ml-2 rounded-full bg-acc-soft px-2 py-0.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.1em] text-acc">You</span>}
            </p>
            <p className="mt-0.5 flex items-center gap-1.5 text-[12px] text-muted-foreground">
              <ActDot tone={act.dot} />{act.label} · {member.display_role}
            </p>
            {member.in_meeting && <div className="mt-1"><InMeeting member={member} /></div>}
          </div>
        </div>
        {/* The member's state line is a sentence about their work, not display copy — DM Sans
          * (Teddy 2026-07-30: the serif is for mastheads and section headings only). */}
        <h1 className="mt-5 max-w-3xl text-[clamp(20px,2.2vw,26px)] font-medium leading-[1.35] tracking-[-.01em]">
          {stateText}
        </h1>
        {ctx.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {ctx.map((c) => (
              <span key={c.lab} className="rounded-full border border-border bg-card px-2.5 py-1 font-['DM_Mono'] text-[10px] text-muted-foreground">
                <span className="uppercase tracking-[.1em] opacity-70">{c.lab} </span>{c.v}
              </span>
            ))}
          </div>
        )}
        <div className="mt-6 border-b border-border" />

        {/* two-column brief — rail (at-a-glance + near-your-work) left on desktop, document right.
            DOM order is main-first so the phone view leads with the live line. */}
        <div className="mt-6 grid items-start gap-8 lg:grid-cols-[.5fr_1fr]">
          <main className="space-y-8 lg:order-2">
            <div>
              <p className="mb-2.5 font-['DM_Mono'] text-[9px] uppercase tracking-[.16em] text-muted-foreground">Working on</p>
              <LiveStatement member={member} />
            </div>

            {blockers.length > 0 && (
              <div>
                <SectionHead label="Blocked" count={blockers.length} />
                <BlockedCallout blockers={blockers} />
              </div>
            )}

            {threads.length > 0 && (
              <div>
                <SectionHead label="Workstreams" count={threads.length} />
                {threads.map((t) => <ThreadRow key={t.name} thread={t} />)}
              </div>
            )}

            <div>
              <SectionHead label="Recent" />
              {highlights.length > 0 ? (
                <div>
                  {highlights.map((item) => <HighlightRow key={item.id} item={item} />)}
                </div>
              ) : (
                <p className="font-['DM_Mono'] text-[12px] italic text-muted-foreground">
                  nothing captured yet. {you ? 'Your' : 'Their'} sources may still be syncing, or it&rsquo;s just a quiet stretch.
                </p>
              )}
            </div>

            {/* everything the highlight didn't already show — day-grouped, so a rolling 24h
                window still files under TODAY / YESTERDAY headers. Absent when there are 3 or
                fewer touchpoints: an empty "and the rest" section is worse than no section. */}
            {rest.length > 0 && (
              <div>
                <SectionHead label="Last 24 hours" count={rest.length} />
                <RecordTimeline items={rest} />
              </div>
            )}
          </main>

          <aside className="space-y-6 lg:order-1">
            <div className="rounded-[20px] border border-border bg-card p-4 shadow-[0_12px_25px_rgba(88,69,42,.05)]">
              <div className="flex items-center justify-between py-1.5 text-[13px]">
                <span className="text-muted-foreground">Activity</span>
                <b className="flex items-center gap-1.5 font-medium"><ActDot tone={act.dot} />{act.label}</b>
              </div>
              {threads.length > 0 && (
                <div className="flex items-center justify-between border-t border-border py-1.5 text-[13px]">
                  <span className="text-muted-foreground">Workstreams</span><b className="font-medium">{threads.length}</b>
                </div>
              )}
              {blockers.length > 0 && (
                <div className="flex items-center justify-between border-t border-border py-1.5 text-[13px]">
                  <span className="text-muted-foreground">Blocked</span><b className="font-medium text-[#b95c4b]">{blockers.length}</b>
                </div>
              )}
              {items.length > 0 && (
                <div className="flex items-center justify-between border-t border-border py-1.5 text-[13px]">
                  <span className="text-muted-foreground">Touchpoints</span>
                  <b className="font-medium">{items.length} <span className="font-normal text-muted-foreground">/ {tools} tool{tools !== 1 ? 's' : ''}</span></b>
                </div>
              )}
            </div>

            {!you && member.overlap && (
              <div>
                <SectionHead label="Near your work" />
                <div className="rounded-[20px] border border-border bg-card p-4">
                  <p className="text-[14px] font-medium leading-5">{member.overlap.headline}</p>
                  {member.overlap.detail && <p className="mt-1 text-[13px] leading-5 text-muted-foreground">{member.overlap.detail}</p>}
                </div>
              </div>
            )}

            {/* Sources live in the rail, not the document: which tools are connected (and which
                are quiet) is reference data about the person, not part of what they did today.
                Split in two so the rail answers both halves of the coverage question — what we
                can see, and what we're blind to. The absent half is dimmed: it's the negative
                space of the connected list, not a second list of equal weight. */}
            {connectedSources.length > 0 && (
              <div>
                <SectionHead label="Connected" count={connectedSources.length} />
                <div>
                  {connectedSources.map((s) => <SourceRow key={s.site} source={s} />)}
                </div>
              </div>
            )}

            {missingSources.length > 0 && (
              <div>
                <SectionHead label="Not connected" count={missingSources.length} />
                <div className="opacity-50">
                  {missingSources.map((s) => <SourceRow key={s.site} source={s} />)}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}
